import {
  Box,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import LoadingContainer from "../Loading/LoadingContainer";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import useLongMachineSetting from "../../hook/useLongMachineSetting";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const ClearOrOnline = ({ onClearCash, onGoOnline, onCancel }) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const [isClosing, setIsClosing] = useState(false);

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();
  const { images } = useWebsiteInfo();

  // 處理棄分按鈕
  const handleClearCash = () => {
    setIsClosing(true);
    onClearCash();
  };

  // 處理轉至線上按鈕
  const handleGoOnline = () => {
    setIsClosing(true);
    onGoOnline();
  };

  // 處理取消按鈕
  const handleCancel = () => {
    setIsClosing(true);
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: "back2",
        musicVolume: 0.2,
      })
    );
    onCancel();
  };

  useEffect(() => {
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        enabled: true,
        song: "settle",
        musicVolume: 0.5,
      })
    );
  }, []);

  const { longMachinePaddingTop } = useLongMachineSetting();

  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.8)"
      pt={longMachinePaddingTop}
      zIndex="9998"
      backdropFilter="blur(2px)"
      justifyContent="center"
      alignItems="center"
    >
      <ChakraBox
        as={Flex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        w="680px"
        h="480px"
        position={"relative"}
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgImage: `url('${COMMON_WEB_PATH}/911TW/hintBG2.webp')`,
            bgPosition: "center",
            bgRepeat: "no-repeat",
            bgSize: "100% 100%",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgImage: `url('${COMMON_WEB_PATH}/911TW/getRealFrame.gif')`,
            bgPosition: "center",
            bgRepeat: "no-repeat",
            bgSize: "100% 100%",
            zIndex: 1,
            margin: "10px",
          },
        }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        p={6}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          position="relative"
          zIndex={2}
          w="100%"
          h="430px"
        >
          {isClosing && <LoadingContainer />}

          <Flex
            position="absolute"
            right="20px"
            top="20px"
            zIndex="10"
            width="50px"
            height="50px"
            borderRadius="full"
            justifyContent="center"
            alignItems="center"
            cursor="pointer"
            onClick={handleCancel}
          >
            <Image src={`${COMMON_WEB_PATH}/911TW/closeBetRecord.webp`} />
          </Flex>

          {/* 標題區域 */}
          <Flex
            fontSize="24px"
            color="white"
            textAlign="center"
            sx={{ width: "-webkit-fill-available" }}
            justifyContent="center"
            alignItems="center"
            h={"130px"}
          >
            <Image
              src={`${COMMON_WEB_PATH}/911TW/warning.webp`}
              height={{
                base: "50px",
                xl: "68px",
              }}
            />
          </Flex>

          {/* 主要內容區域 */}
          <Flex
            w="100%"
            h={"150px"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Box
              fontSize="48px"
              fontWeight="black"
              color="white"
              textAlign="center"
              sx={{
                background:
                  "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
              }}
            >
              {i18n("請選擇操作方式")}
            </Box>
          </Flex>

          {/* 按鈕區域 */}
          <Flex
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            h={"130px"}
          >
            <Flex gap={4} mb={4} justifyContent="center" alignItems="center">
              {/* 棄分按鈕 */}
              <Flex
                as="button"
                w="100%"
                minW={"200px"}
                justifyContent="center"
                alignItems="center"
                cursor="pointer"
                onClick={handleClearCash}
                position="relative"
                px={8}
                py={3}
                borderRadius="full"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-1px)",
                  filter: "brightness(1.1)",
                }}
                _active={{
                  transform: "translateY(0px)",
                }}
                bgGradient="linear(to-b, #ba4cbb, #b42cbe, #b52cc5)"
                // 外層陰影光暈
                boxShadow={`
        0 0 20px rgba(168, 85, 247, 0.4),
        0 4px 15px rgba(124, 58, 237, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.3),
        inset 0 -2px 0 rgba(124, 58, 237, 0.8)
      `}
                border="2px solid"
                borderColor="#e09fe5"
              >
                <Text
                  fontSize="28px"
                  fontWeight="bold"
                  color="white"
                  sx={{
                    background:
                      "linear-gradient(180deg, #fff 0%,#fff 60%,  #fff 80%,  #fff 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0px 1px 5px rgba(186, 76, 187, 0.4)",
                  }}
                >
                  {i18n("棄分")}
                </Text>
              </Flex>

              {/* 轉至線上按鈕 */}
              <Flex
                as="button"
                w="100%"
                minW={"200px"}
                justifyContent="center"
                alignItems="center"
                cursor="pointer"
                onClick={handleGoOnline}
                position="relative"
                px={8}
                py={3}
                borderRadius="full"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-1px)",
                  filter: "brightness(1.1)",
                }}
                _active={{
                  transform: "translateY(0px)",
                }}
                bgGradient="linear(to-b, #ba4cbb, #b42cbe, #b52cc5)"
                // 外層陰影光暈
                boxShadow={`
        0 0 20px rgba(168, 85, 247, 0.4),
        0 4px 15px rgba(124, 58, 237, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.3),
        inset 0 -2px 0 rgba(124, 58, 237, 0.8)
      `}
                border="2px solid"
                borderColor="#e09fe5"
              >
                <Text
                  fontSize="28px"
                  fontWeight="bold"
                  color="white"
                  sx={{
                    background:
                      "linear-gradient(180deg, #fff 0%,#fff 60%,  #fff 80%,  #fff 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0px 1px 5px rgba(186, 76, 187, 0.4)",
                  }}
                >
                  {i18n("轉至線上")}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ChakraBox>
    </Flex>
  );
};

export default ClearOrOnline;
